"use client";

import React, { useState, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Breadcrumb from "@/components/common/Breadcrumb ";
import ProductListCard from "@/components/common/ProductListCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { useNewGetAllCategories } from "@/hooks/ApiHooks/newProductListingHooks/newGetAllCategoriesHook";
import dynamic from "next/dynamic";

const CategoryAccordionSkelton = dynamic(() => import("@/components/common/skeltons/CategoryAccordionSkelton"), { ssr: false });
const AllProductCardSkelton = dynamic(() => import("@/components/common/skeltons/AllProductCardSkelton"), { ssr: false });
import { Product, Category, SubCategory } from "@/types/allProductPageTypes";
import SortDropdown from "@/components/common/SortDropDown";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { productCategoryMetaTags } from "../meta-tags";
import { useNewGetAllFilteredProducts } from "@/hooks/ApiHooks/newProductListingHooks/getNewRequiredProductHook";
const ProductCategoryInfo = dynamic(() => import("@/components/product-category/ProductCategoryInfo"), {
  ssr: true, // Keep SSR for SEO as it contains descriptive content
  loading: () => <div className="h-40 bg-gray-50 animate-pulse rounded-lg mt-8" />
});

export default function ProductListing() {

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathSegments = pathname.split("/");
  const categorySlugFromUrl = pathSegments[2];
  const subCategorySlugFromUrl = pathSegments[3];
  const categoryNameFromUrl = pathSegments[2]
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const searchTerm = searchParams.get("search") || "";
  const productMeta = productCategoryMetaTags[categorySlugFromUrl];

  const [openAccordion, setOpenAccordion] = useState(categorySlugFromUrl || "");
  const [categoryName, setCategoryName] = useState(categoryNameFromUrl || "All Products");
  const [selectedStatus, setSelectedStatus] = useState("");

  const pageFromUrl = Number(searchParams.get("page")) || 1;
  const [pageNumber, setPageNumber] = useState(pageFromUrl);
  const [totalPages, setTotalPages] = useState(1);
  const [parameters, setParameters] = useState({
    id: 0,
    // brandName: "",
    pageNumber: pageNumber,
    stockStatus: "",
    value: "",
    order: "asc",
    search: searchTerm || "",
    categorySlug: subCategorySlugFromUrl || categorySlugFromUrl || "",
  });

  // Sync parameters with URL parameters and filters
  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    setPageNumber(page);
    setParameters((prev) => ({
      ...prev,
      pageNumber: page,
      search: searchTerm,
      categorySlug: subCategorySlugFromUrl || categorySlugFromUrl || "",
      stockStatus: selectedStatus,
    }));
  }, [searchParams, categorySlugFromUrl, subCategorySlugFromUrl, selectedStatus, searchTerm]);

  const options: { name: string; value: string; order: string }[] = [
    { name: "Sort by popularity", value: "popularity", order: "asc" },
    { name: "Sort by rating", value: "rating", order: "asc" },
    // { name: "Sort by price: low to high", value: "price_low_high", order: "asc" },
    // { name: "Sort by price: high to low", value: "price_high_low", order: "desc" },
  ];

  const { data: newCategoryData } = useNewGetAllCategories();
  const { data: newFilteredData, isLoading } = useNewGetAllFilteredProducts(parameters);
  const allFilteredProducts = newFilteredData?.data?.result?.items;
  const categoryContent = newFilteredData?.data?.result?.category ?? null;

  useEffect(() => {
    if (newFilteredData?.data?.result?.total_pages) {
      setTotalPages(newFilteredData?.data?.result?.total_pages);
    }
  }, [newFilteredData?.data?.result?.total_pages]);

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    // Navigate to the new category URL
    const categorySlug = category.slug || "all-products";
    router.push(`/product-category/${categorySlug}`);

    // Optimistically update state (optional, but good for UI responsiveness before URL change propagates)
    setCategoryName(category.name);
    setOpenAccordion(category.slug || "");
    // setOpenAccordion(category.id.toString());
  };

  // Handle subcategory selection
  const handleSubCategorySelect = (subCategory: SubCategory) => {
    // Keeping consistent with previous logic's intent but cleaner:
    const categorySlug = categorySlugFromUrl || "all-products";
    const subCategorySlug = subCategory.slug?.toLowerCase().replace(/\s+/g, "-") || "";
    const newPath = `/product-category/${categorySlug}/${subCategorySlug}`;
    router.push(newPath);
  };

  // Handle accordion state changes
  const handleAccordionChange = (value: string | undefined) => {
    setOpenAccordion(value === openAccordion ? "" : value || "");
  };

  // Prepare breadcrumbs
  const breadcrumbs = [
    { name: "Home", href: "/" },
    // { name: "Shop", href: "/product-category/all-products" },
    ...(searchTerm
      ? [
        {
          name: `Search Results For "${searchTerm}"`,
          href: `/product-category/all-products?search=${searchTerm}`,
        },
      ]
      : [
        {
          name: categoryName,
          href: `/product-category/${categoryName
            .toLowerCase()
            .replace(/\s+/g, "-")}`,
        },
        ...(subCategorySlugFromUrl
          ? [
            {
              name: subCategorySlugFromUrl,
              href: `/product-category/${categoryName
                .toLowerCase()
                .replace(/\s+/g, "-")}/${subCategorySlugFromUrl
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`,
            },
          ]
          : []),
      ]),
  ].filter(
    (breadcrumb): breadcrumb is { name: string; href: string } =>
      breadcrumb !== null
  );

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > Number(totalPages)) return;

    const params = new URLSearchParams(searchParams.toString());
    params.set("page", newPage.toString());
    router.push(`${pathname}?${params.toString()}`, { scroll: false });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStatusCheckboxChange = (status: string) => {
    setSelectedStatus((prev) => (prev === status ? "" : status));

    const params = new URLSearchParams(searchParams.toString());
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const processedCategories = (Array.isArray(newCategoryData?.result) ? newCategoryData?.result : [])
    .filter((category: Category) =>
      category?.name !== "Accessories" &&
      category?.name !== "Metal Products"
    )
    .sort((a: Category, b: Category) => {
      if (a?.name === "All Products") return -1;
      if (b?.name === "All Products") return 1;
      return 0;
    });

  useEffect(() => {
    // Only update the EXISTING title tag, don't create new ones
    document.title = productMeta?.title || "Products";

    // Update or create meta description
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', productMeta?.description || "");

    // Update or create canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', productMeta?.canonical || "https://geminiuae.com/product-category/all-products");
  }, [productMeta]);

  return (
    <>
      <div>
        <Breadcrumb
          breadcrumbs={breadcrumbs}
          showBack={true}
          backText="Go Back"
        />
      </div>
      <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto lg:flex lg:gap-web-40">
        {/* Product categories, By brands, Product Status */}
        <div className="basis-[22.29vw] lg:min-w-[22.29vw] lg:max-w-[22.29vw] lg:w-[22.29vw] rounded-lg lg:h-fit lg:py-web-20 px-web-5 hidden lg:block lg:sticky lg:top-web-20 lg:self-start">
          {/* Product Categories */}
          <div className="mb-web-45">
            <h5 className="text-web-22 font-medium text-PrimaryLightBlue px-web-20 border-b border-[#e1e1e1a] pb-web-15 leading-[100%]">
              Product Categories
            </h5>
            <ul className="py-web-8">
              {/* processedCategories */}
              {processedCategories ? (
                processedCategories?.map((category: Category) => {
                  return (
                    <li
                      key={category.id}
                      className="px-web-20 py-web-14 block"
                    >
                      <Accordion
                        type="single"
                        // value="1"
                        // value={"965"}
                        value={openAccordion}
                        onValueChange={handleAccordionChange}
                        collapsible
                      >
                        <AccordionItem
                          key={category.id}
                          value={category?.slug || ""}
                          className="border-none"
                        >
                          <AccordionTrigger
                            // className={`hover:text-black transition duration-150 ease-in !text-web-20 leading-[100%] capitalize`}
                            className={`hover:text-black transition duration-150 ease-in !text-web-20 leading-[100%] capitalize
                              ${openAccordion === (category?.slug?.toString() ?? "")
                                ? "text-black font-medium"
                                : "text-[#888] font-normal"
                              }`}
                            onClick={() => handleCategorySelect(category)}
                          >
                            {category.name.replace(/&amp;/g, "&")}
                            {!(
                              (category?.subcategories ?? []).length === 0
                            ) && (
                                <ChevronDown className="w-mob-22 sm:w-tab-22 mt-mob-3 lg:w-web-22 h-fit shrink-0 transition-transform duration-200 text-[#888]" />
                              )}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="sm:max-h-[30vh] sm:overflow-y-scroll sm:scrollbar-hide">
                              {newCategoryData ? (
                                category.subcategories && category.subcategories.length > 0 ? (
                                  category.subcategories.map(
                                    (
                                      subCategory: SubCategory,
                                      index: number
                                    ) => (
                                      <li
                                        key={index}
                                        // className={`cursor-pointer hover:text-black transition duration-150 ease-in py-web-14 last:pb-0 px-web-12 text-web-18 font-normal leading-[100%] capitalize`}
                                        className={`cursor-pointer hover:text-black transition duration-150 ease-in py-web-14 last:pb-0 px-web-12 text-web-18 font-normal leading-[100%] capitalize
                                          ${subCategorySlugFromUrl === subCategory.slug
                                            ? "text-black"
                                            : "text-[#888]"
                                          }`}
                                        onClick={() =>
                                          handleSubCategorySelect(subCategory)
                                        }
                                      >
                                        {subCategory.name.replace(
                                          /&amp;/g,
                                          "&"
                                        )}
                                        {/* Hai */}
                                      </li>
                                    )
                                  )
                                ) : (
                                  ""
                                )
                              ) : (
                                <div className="cursor-pointer hover:text-black transition duration-150 ease-in py-web-14 last:pb-0 px-web-12 text-web-18 font-normal text-[#888] leading-[100%] capitalize">
                                  {!(
                                    category.id == 1525 ||
                                    category.id == 755 ||
                                    category.id == 52
                                  ) && "Loading..."}
                                </div>
                              )}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </li>
                  );
                })
              ) : (
                <CategoryAccordionSkelton />
              )}
            </ul>
          </div>

          {/* Product Status */}
          <div className="mb-web-45">
            <h5 className="text-web-22 font-medium text-PrimaryLightBlue px-web-20 border-b border-[#e1e1e1a] pb-web-15 leading-[100%]">
              Product Status
            </h5>
            <ul className="py-web-8">
              <li className="px-web-20 py-web-14 flex items-center gap-web-10 hover:text-black transition duration-150 ease-in !text-web-20 font-normal text-[#888] leading-[100%] capitalize">
                <Checkbox
                  id="instock"
                  checked={selectedStatus === "instock"}
                  onCheckedChange={() => handleStatusCheckboxChange("instock")}
                />
                <label htmlFor="instock" className="cursor-pointer select-none">
                  In stock
                </label>
              </li>
              <li className="px-web-20 py-web-14 flex items-center gap-web-10 hover:text-black transition duration-150 ease-in !text-web-20 font-normal text-[#888] leading-[100%] capitalize">
                <Checkbox
                  id="outofstock"
                  checked={selectedStatus === "outofstock"}
                  onCheckedChange={() =>
                    handleStatusCheckboxChange("outofstock")
                  }
                />
                <label
                  htmlFor="outofstock"
                  className="cursor-pointer select-none"
                >
                  Out of stock
                </label>
              </li>
              <li className="px-web-20 py-web-14 flex items-center gap-web-10 hover:text-black transition duration-150 ease-in !text-web-20 font-normal text-[#888] leading-[100%] capitalize">
                <Checkbox
                  id="on-sale"
                  checked={selectedStatus === "onbackorder"}
                  onCheckedChange={() =>
                    handleStatusCheckboxChange("onbackorder")
                  }
                />
                <label htmlFor="on-sale" className="cursor-pointer select-none">
                  On sale
                </label>
              </li>
            </ul>
          </div>
        </div>

        {/* right list item section */}
        <div className="flex flex-col gap-mob-10 sm:gap-tab-10 lg:gap-web-10 lg:w-[75%] lg:max-w-[75%]">
          <SortDropdown
            options={options}
            setParameters={(params) => setParameters({ ...params, search: "", categorySlug: subCategorySlugFromUrl || categorySlugFromUrl || "" })}
            setPageNumber={setPageNumber}
            brandName={[]}
            stockStatus={selectedStatus}
            // categoryId={productListingId}
          />
          {/* list items */}
          <ul className="flex flex-wrap gap-mob-20 sm:gap-tab-20 lg:gap-web-40">
            {/* All Filtered Products */}
            <ul className="flex flex-wrap gap-mob-20 sm:gap-tab-20 lg:gap-web-40">
              {isLoading ? (
                <AllProductCardSkelton />
              ) : allFilteredProducts?.length > 0 ? (
                allFilteredProducts.map((product: Product, index: number) => (
                  <li
                    key={index}
                    className="w-full sm:w-[calc(100%/2-1.31vw)] lg:min-w-[20vw] lg:w-[calc(100%/3-1.39vw)]"
                  >
                    <ProductListCard key={index} product={product} priority={index < 3} />
                  </li>
                ))
              ) : (
                <div className="w-[95vw] sm:w-[95vw] lg:w-[60vw] max-w-full flex justify-center items-center font-semibold mt-mob-30 sm:mt-tab-30 lg:mt-web-100 text-mob-20 sm:text-tab-25 lg:text-web-35">
                  No products found for the selected filter
                </div>
              )}
            </ul>
          </ul>

          <div className="mt-mob-20 sm:mt-tab-20 lg:mt-web-50 mx-auto">
            {/* pagination */}
            <Pagination>
              <PaginationContent>
                {/* Previous Page Button */}
                <PaginationItem>
                  <PaginationPrevious
                    onClick={
                      pageNumber > 1
                        ? () => handlePageChange(pageNumber - 1)
                        : undefined
                    }
                    className={cn(
                      "cursor-pointer transition-all", // Keep default styles
                      pageNumber <= 1
                        ? "opacity-50 cursor-not-allowed bg-White text-PrimaryLightBlue border-PrimaryLightBlue flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-20 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border"
                        : "bg-White text-PrimaryLightBlue border-PrimaryLightBlue flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-20 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border" // Only disable clicks without breaking layout
                    )}
                    aria-disabled={pageNumber <= 1} // Improves accessibility
                    tabIndex={pageNumber <= 1 ? -1 : undefined} // Prevents tab navigation if disabled
                  />
                </PaginationItem>

                {/* Previous Page (if exists) */}
                {pageNumber > 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => handlePageChange(pageNumber - 1)}
                    >
                      {pageNumber - 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Current Page */}
                <PaginationItem>
                  <PaginationLink isActive>{pageNumber}</PaginationLink>
                </PaginationItem>

                {/* Next Page (if exists) */}
                {pageNumber < Number(totalPages) && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => handlePageChange(pageNumber + 1)}
                    >
                      {pageNumber + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Next Page Button */}
                <PaginationItem>
                  <PaginationNext
                    onClick={
                      pageNumber < Number(totalPages)
                        ? () => handlePageChange(pageNumber + 1)
                        : undefined
                    }
                    className={cn(
                      "cursor-pointer transition-all", // Keep default styles
                      pageNumber >= Number(totalPages)
                        ? "opacity-50 cursor-not-allowed bg-White text-PrimaryLightBlue border-PrimaryLightBlue flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-15 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border"
                        : "bg-White text-PrimaryLightBlue border-PrimaryLightBlue flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-20 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border" // Disable clicks without breaking layout
                    )}
                    aria-disabled={pageNumber >= Number(totalPages)} // Improves accessibility
                    tabIndex={pageNumber >= Number(totalPages) ? -1 : undefined} // Prevents tab navigation if disabled
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
          <ProductCategoryInfo category={categoryContent} />
        </div>
      </div>
    </>
  );
}
